import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo, faqs, testimonials } from '@/mocks/homeData';
import { categories } from '@/mocks/categoryData';
import { articles } from '@/mocks/articleData';

function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all duration-300"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-100/50 transition-colors cursor-pointer"
          >
            <span className="text-sm font-medium text-foreground-800 pr-4">
              {faq.question}
            </span>
            <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openIndex === index ? 'rotate-45' : ''}`}>
              <i className="ri-add-line text-foreground-500"></i>
            </span>
          </button>
          <div
            className={`transition-all duration-300 overflow-hidden ${
              openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed border-t border-background-200/50 pt-3">
              {faq.answer}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CounterStat({ value, label, suffix }: { value: string; label: string; suffix: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const numericValue = parseInt(value.replace(/[^0-9]/g, ''), 10);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const duration = 2000;
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numericValue));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [numericValue]);

  return (
    <div ref={ref} className="text-center">
      <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-sm text-foreground-500 mt-1">{label}</div>
    </div>
  );
}

export default function Home() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '禁漫天堂 - 官方入口与完整使用指南',
    description: '禁漫天堂是深受用户喜爱的成人漫画平台，提供最新访问入口、注册登录教程、内容分类导航及常见问题解答。',
    url: 'https://jmtthub.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '禁漫天堂',
      url: 'https://jmtthub.com/',
    },
    about: {
      '@type': 'Organization',
      name: '禁漫天堂',
      description: '国内领先的成人漫画在线阅读平台',
    },
    mainEntity: {
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      {/* Hero Section */}
      <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20warm%20toned%20manga%20inspired%20background%20with%20artistic%20brush%20strokes%2C%20soft%20gradients%20of%20coral%20and%20amber%2C%20elegant%20Japanese%20wave%20patterns%20in%20subtle%20gold%2C%20minimalist%20composition%20with%20flowing%20lines%2C%20warm%20atmospheric%20lighting%2C%20digital%20art%20style%2C%20sophisticated%20and%20modern%20aesthetic%2C%20no%20text&width=1920&height=1080&seq=hero-jmtt-01&orientation=landscape"
            alt="禁漫天堂 - 成人漫画在线阅读平台"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 text-center pt-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></span>
            <span className="text-xs text-white/90">2026年8月最新入口已更新</span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight max-w-3xl mx-auto">
            禁漫天堂
            <span className="block text-xl md:text-2xl lg:text-3xl font-body font-normal mt-3 text-white/80">
              最受欢迎的成人漫画在线阅读平台
            </span>
          </h1>

          <p className="mt-6 text-sm md:text-base text-white/70 max-w-xl mx-auto leading-relaxed">
            {siteInfo.description}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#"
              className="inline-flex items-center gap-2 px-7 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg text-sm transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
            >
              <i className="ri-external-link-line"></i>
              访问最新入口
            </a>
            <Link
              to="/article/register-guide"
              className="inline-flex items-center gap-2 px-7 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg text-sm backdrop-blur-sm border border-white/20 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              注册教程
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Access Links */}
      <section className="relative -mt-10 z-20 w-full px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: 'ri-link', label: '最新入口', desc: '官方主站' },
              { icon: 'ri-git-branch-line', label: '备用镜像', desc: '稳定访问' },
              { icon: 'ri-download-2-line', label: 'APP下载', desc: '安卓/iOS' },
              { icon: 'ri-book-read-line', label: '使用教程', desc: '快速上手', path: '/article/register-guide' },
            ].map((item, index) => (
              item.path ? (
                <Link
                  key={index}
                  to={item.path}
                  className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 group-hover:bg-primary-500 transition-colors duration-300 flex-shrink-0">
                    <i className={`${item.icon} text-primary-600 group-hover:text-white text-lg transition-colors`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800">{item.label}</div>
                    <div className="text-xs text-foreground-500">{item.desc}</div>
                  </div>
                </Link>
              ) : (
                <a
                  key={index}
                  href="#"
                  className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 group-hover:bg-primary-500 transition-colors duration-300 flex-shrink-0">
                    <i className={`${item.icon} text-primary-600 group-hover:text-white text-lg transition-colors`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800">{item.label}</div>
                    <div className="text-xs text-foreground-500">{item.desc}</div>
                  </div>
                </a>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              为什么选择禁漫天堂？
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
              数百万用户的共同选择，用实力打造极致漫画阅读体验
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {siteInfo.features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-accent-300 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 group-hover:bg-accent-500 transition-colors duration-300 mb-4">
                  <i className={`${feature.icon} text-accent-600 group-hover:text-white text-xl transition-colors`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">{feature.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="w-full px-4 md:px-6 py-14 md:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <CounterStat value="10万+" label="漫画资源" suffix="+" />
            <CounterStat value="500万+" label="注册用户" suffix="万+" />
            <CounterStat value="300+" label="每日更新" suffix="+" />
            <CounterStat value="99.9%" label="服务可用率" suffix="%" />
          </div>
        </div>
      </section>

      {/* Category Preview */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                漫画分类导航
              </h2>
              <p className="mt-2 text-sm text-foreground-500">探索丰富多样的漫画类型，找到你的最爱</p>
            </div>
            <Link
              to="/category/riman"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              查看全部分类
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative overflow-hidden rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 mb-3 line-clamp-2">{cat.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400">{cat.count.toLocaleString()} 部作品</span>
                    <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-500 transition-colors duration-300 flex-shrink-0">
                      <i className="ri-arrow-right-line text-xs text-primary-500 group-hover:text-white transition-colors"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                最新使用指南
              </h2>
              <p className="mt-2 text-sm text-foreground-500">从入门到精通，全面掌握平台使用技巧</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.slice(0, 3).map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden cursor-pointer"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                      {article.category}
                    </span>
                    <span className="text-xs text-foreground-400">{article.date}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-1.5 text-xs text-foreground-400">
                    <i className="ri-time-line"></i>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              常见问题解答
            </h2>
            <p className="mt-2 text-sm text-foreground-500">快速了解禁漫天堂，解答您的疑问</p>
          </div>
          <FAQAccordion />
        </div>
      </section>

      {/* Testimonials */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              用户真实评价
            </h2>
            <p className="mt-2 text-sm text-foreground-500">听听我们的用户怎么说</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-xl border border-background-200/70 p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-heading font-bold text-sm flex-shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                    <div className="text-xs text-foreground-400">{t.date}</div>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-2">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-background-50 rounded-2xl border border-background-200/70 p-10 md:p-14">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-100 mx-auto mb-6">
              <i className="ri-rocket-2-line text-2xl text-primary-500"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              准备好了吗？立即开启漫画之旅
            </h2>
            <p className="text-sm text-foreground-500 mb-8 max-w-md mx-auto">
              海量高清漫画等你来发现，注册完全免费，一分钟即可开始阅读
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="#"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg text-sm transition-all duration-300 whitespace-nowrap cursor-pointer hover:scale-105"
              >
                <i className="ri-external-link-line"></i>
                进入禁漫天堂
              </a>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-secondary-100 hover:bg-secondary-200 text-foreground-700 font-medium rounded-lg text-sm transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-information-line"></i>
                了解更多
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}